import React, { useState, useEffect } from 'react';
import { Notification } from '../api/notifications';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from './ui/BrandModal';

interface EditNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  notification: Notification | null;
  onSubmit: (id: string, data: Partial<Notification>) => Promise<void>;
}

const EditNotificationModal: React.FC<EditNotificationModalProps> = ({
  isOpen,
  onClose,
  notification,
  onSubmit,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    type: 'general' as Notification['type'],
    title: '',
    message: '',
    priority: 'medium' as Notification['priority'],
    entityType: undefined as Notification['entityType'],
    entityId: '',
    scheduledFor: '',
    expiresAt: '',
  });

  // Update form data when notification changes
  useEffect(() => {
    if (notification) {
      setFormData({
        type: notification.type,
        title: notification.title,
        message: notification.message,
        priority: notification.priority,
        entityType: notification.entityType,
        entityId: notification.entityId || '',
        scheduledFor: notification.scheduledFor ? 
          new Date(notification.scheduledFor).toISOString().slice(0, 16) : '',
        expiresAt: notification.expiresAt ? 
          new Date(notification.expiresAt).toISOString().slice(0, 16) : '',
      });
    }
  }, [notification]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!notification) return;

    setLoading(true);

    try {
      // Clean up the data before submitting
      const submitData: Partial<Notification> = {
        ...formData,
        entityId: formData.entityId || undefined,
        scheduledFor: formData.scheduledFor || undefined,
        expiresAt: formData.expiresAt || undefined,
      };

      await onSubmit(notification.id, submitData);
    } catch (error) {
      console.error('Error updating notification:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  if (!notification) return null;

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader title="Edit Notification" onClose={onClose} />
      <form onSubmit={handleSubmit}>
        <BrandModalBody>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">                    {/* Type */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700">
                        Type *
                      </label>
                      <select
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      >
                        <option key="general-1753461297306" value="general">General</option>
                        <option key="breeding-1753461297306" value="breeding">Breeding</option>
                        <option key="health-1753461297306" value="health">Health</option>
                        <option key="task-1753461297306" value="task">Task</option>
                        <option key="supply_alert-1753461297306" value="supply_alert">Supply Alert</option>
                        <option key="equipment_alert-1753461297306" value="equipment_alert">Equipment Alert</option>
                        <option key="disease_alert-1753461297306" value="disease_alert">Disease Alert</option>
                        <option key="financial_alert-1753461297306" value="financial_alert">Financial Alert</option>
                        <option key="customer_notification-1753461297306" value="customer_notification">Customer Notification</option>
                        <option key="document_expiry-1753461297306" value="document_expiry">Document Expiry</option>
                        <option key="system-1753461297306" value="system">System</option>
                        <option key="alert-1753461297306" value="alert">Alert</option>
                      </select>
                    </div>

                    {/* Priority */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700">
                        Priority *
                      </label>
                      <select
                        name="priority"
                        value={formData.priority}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      >
                        <option key="low-1753461297306" value="low">Low</option>
                        <option key="medium-1753461297306" value="medium">Medium</option>
                        <option key="high-1753461297306" value="high">High</option>
                        <option key="urgent-1753461297306" value="urgent">Urgent</option>
                        <option key="critical-1753461297306" value="critical">Critical</option>
                      </select>
                    </div>

                    {/* Title */}
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-slate-700">
                        Title *
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                        placeholder="Enter notification title"
                      />
                    </div>

                    {/* Message */}
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-slate-700">
                        Message *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={3}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                        placeholder="Enter notification message"
                      />
                    </div>

                    {/* Entity Type */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700">
                        Entity Type
                      </label>
                      <select
                        name="entityType"
                        value={formData.entityType || ''}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      >
                        <option key="empty-option-1753461297306" value="">Select entity type...</option>
                        <option key="breeding_pair-1753461297306" value="breeding_pair">Breeding Pair</option>
                        <option key="bird-1753461297306" value="bird">Bird</option>
                        <option key="species-1753461297306" value="species">Species</option>
                        <option key="clutch-1753461297306" value="clutch">Clutch</option>
                        <option key="egg-1753461297306" value="egg">Egg</option>
                        <option key="cage-1753461297306" value="cage">Cage</option>
                        <option key="task-1753461297306" value="task">Task</option>
                        <option key="user-1753461297306" value="user">User</option>
                        <option key="feed_stock-1753461297306" value="feed_stock">Feed Stock</option>
                        <option key="supply-1753461297306" value="supply">Supply</option>
                        <option key="equipment-1753461297306" value="equipment">Equipment</option>
                        <option key="health_test-1753461297306" value="health_test">Health Test</option>
                        <option key="disease_record-1753461297306" value="disease_record">Disease Record</option>
                        <option key="document-1753461297306" value="document">Document</option>
                        <option key="transaction-1753461297306" value="transaction">Transaction</option>
                      </select>
                    </div>

                    {/* Entity ID */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700">
                        Entity ID
                      </label>
                      <input
                        type="text"
                        name="entityId"
                        value={formData.entityId}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                        placeholder="Enter entity ID"
                      />
                    </div>

                    {/* Scheduled For */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700">
                        Scheduled For
                      </label>
                      <input
                        type="datetime-local"
                        name="scheduledFor"
                        value={formData.scheduledFor}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      />
                    </div>

                    {/* Expires At */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700">
                        Expires At
                      </label>
                      <input
                        type="datetime-local"
                        name="expiresAt"
                        value={formData.expiresAt}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  {/* Notification Info */}
                  <div className="mt-6 p-4 bg-slate-50/80 rounded-lg">
                    <h4 className="text-sm font-medium text-slate-900 mb-2">Notification Info</h4>
                    <div className="grid grid-cols-2 gap-4 text-xs text-slate-600">
                      <div>
                        <span className="font-medium">Status:</span> {notification.status}
                      </div>
                      <div>
                        <span className="font-medium">Created:</span> {new Date(notification.createdAt).toLocaleString()}
                      </div>
                      <div>
                        <span className="font-medium">Updated:</span> {new Date(notification.updatedAt).toLocaleString()}
                      </div>
                      {notification.readAt && (
                        <div>
                          <span className="font-medium">Read:</span> {new Date(notification.readAt).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>

        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary text-sm disabled:opacity-50"
          >
            {loading ? 'Updating...' : 'Update Notification'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
};

export default EditNotificationModal; 